import { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Zap, Flame, Timer } from 'lucide-react';
import ProductCard from '@/components/products/ProductCard';
import AnimatedSection from './AnimatedSection';

const FlashOffersCarousel = ({ products: rawProducts, loading }) => {
  const products = Array.isArray(rawProducts) ? rawProducts : [];
  const scrollRef = useRef(null);
  const [timeLeft, setTimeLeft] = useState({ hours: 23, minutes: 59, seconds: 59 });

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev.seconds > 0) return { ...prev, seconds: prev.seconds - 1 };
        if (prev.minutes > 0) return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        if (prev.hours > 0) return { hours: prev.hours - 1, minutes: 59, seconds: 59 };
        return { hours: 23, minutes: 59, seconds: 59 };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === 'left' ? -280 : 280;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  if (!loading && (!products || products.length === 0)) return null;

  return (
    <AnimatedSection>
      <section
        className="py-8 md:py-12 bg-gradient-to-r from-[#FF4500]/10 via-[#FF4500]/5 to-[#FF4500]/10 rounded-2xl mx-4 md:mx-0"
        data-testid="flash-offers-section"
      >
        <div className="container mx-auto px-4 max-w-7xl">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-[#FF4500] rounded-xl shadow-lg shadow-[#FF4500]/30">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-slate-900">Ofertas Relampago</h2>
                <p className="text-orange-600 text-sm font-medium flex items-center gap-1">
                  <Flame className="w-3.5 h-3.5" /> Acaba em breve!
                </p>
              </div>
            </div>

            {/* Countdown */}
            <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
              <Timer className="w-4 h-4 text-[#FF4500]" />
              <div className="flex items-center gap-1 font-mono font-bold">
                <span className="bg-slate-900 text-white px-2 py-1 rounded text-sm">
                  {String(timeLeft.hours).padStart(2, '0')}
                </span>
                <span className="text-slate-400">:</span>
                <span className="bg-slate-900 text-white px-2 py-1 rounded text-sm">
                  {String(timeLeft.minutes).padStart(2, '0')}
                </span>
                <span className="text-slate-400">:</span>
                <span className="bg-[#FF4500] text-white px-2 py-1 rounded text-sm">
                  {String(timeLeft.seconds).padStart(2, '0')}
                </span>
              </div>
            </div>
          </div>

          {/* Carousel */}
          <div className="relative">
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 z-10 w-10 h-10 bg-white shadow-lg rounded-full flex items-center justify-center hover:bg-slate-50 transition-colors hidden md:flex"
            >
              <ChevronLeft className="w-5 h-5 text-slate-600" />
            </button>
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 z-10 w-10 h-10 bg-white shadow-lg rounded-full flex items-center justify-center hover:bg-slate-50 transition-colors hidden md:flex"
            >
              <ChevronRight className="w-5 h-5 text-slate-600" />
            </button>

            <div ref={scrollRef} className="flex gap-3 overflow-x-auto hide-scrollbar scroll-smooth pb-2">
              {loading
                ? [...Array(5)].map((_, i) => (
                    <div key={i} className="flex-shrink-0 w-[200px] md:w-[220px]">
                      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
                        <div className="skeleton aspect-square w-full" />
                        <div className="p-3 space-y-2">
                          <div className="skeleton h-4 w-full" />
                          <div className="skeleton h-5 w-1/2" />
                        </div>
                      </div>
                    </div>
                  ))
                : products.map((product, index) => (
                    <div
                      key={product.id}
                      className="flex-shrink-0 w-[200px] md:w-[220px]"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <ProductCard product={product} compact />
                    </div>
                  ))}
            </div>
          </div>
        </div>
      </section>
    </AnimatedSection>
  );
};

export default FlashOffersCarousel;
